/**
 * /mail/webhooks — customer-configurable event delivery (OCP-171).
 *
 * Modal-driven UX:
 *   - List with URL, events chips, status, last-delivery indicator
 *   - "+ Add webhook" → create modal → reveal modal with plaintext secret
 *   - Per-row: Test (fire synthetic event), Deliveries (side panel
 *     with last-100 attempts), Edit (modal), Rotate secret (reveal-
 *     once modal), Delete (confirmation)
 *   - Code snippet at the bottom showing how to verify the signature
 *     in Node — same canonical HMAC-SHA256 pattern as OCP↔OMS internal.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MailReadOnlyNotice } from '../components/MailReadOnlyNotice';
import { ApiError, api } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';
import { useTransientState } from '../lib/useTransientState';
import { fmtDateTime } from '../lib/format-time';

interface WebhookSummary {
  id: string;
  url: string;
  label: string | null;
  event_filters: string[];
  enabled: boolean;
  created_at: string | null;
  last_delivery_status: string | null;
}

interface WebhookWithSecret extends WebhookSummary {
  secret: string;
}

interface DeliveryRow {
  id: string;
  event: string;
  response_status: number | null;
  response_excerpt: string | null;
  attempts: number;
  delivered_at: string | null;
  is_retry: boolean;
}

interface ListResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  webhooks: WebhookSummary[];
}

type Modal =
  | { kind: 'create' }
  | { kind: 'edit'; webhook: WebhookSummary }
  | { kind: 'reveal'; minted: WebhookWithSecret; rotated: boolean }
  | { kind: 'deliveries'; webhook: WebhookSummary; rows: DeliveryRow[] | null }
  | { kind: 'confirm-delete'; webhook: WebhookSummary }
  | null;

const EVENT_TYPES = [
  'delivery',
  'bounce.permanent',
  'bounce.transient',
  'complaint',
  'inbound.received',
  // OCP-230 / OMS-76 — sender-domain DKIM lifecycle events.
  'senderDomain.dkim.migrated',
  'senderDomain.dkim.rotation_pending',
  'senderDomain.dkim.rotation_complete',
] as const;

export function MailWebhooksPage() {
  const { t } = useTranslation('mail-webhooks');
  const [data, setData] = useState<ListResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  const [testFlash, setTestFlash] = useTransientState<string | null>(null, 4000);
  // OCP-545 - webhook wiring is mail configuration: owner/admin only.
  const { canWrite } = useMailWriteAccess();

  const load = useCallback(async (signal?: AbortSignal) => {
    setState('loading');
    setErrorDetail(null);
    try {
      const r = await api<ListResponse>('/v1/portal/mail/webhooks', { signal });
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setErrorDetail(t('error.load'));
      setState('error');
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  async function createWebhook(form: WebhookFormValue): Promise<void> {
    try {
      const minted = await api<WebhookWithSecret>('/v1/portal/mail/webhooks', {
        method: 'POST',
        body: form,
      });
      setModal({ kind: 'reveal', minted, rotated: false });
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setErrorDetail(t('error.invalidUrlDev'));
      } else {
        setErrorDetail(t('error.create'));
      }
      setModal(null);
    }
  }

  async function patchWebhook(id: string, form: WebhookFormValue): Promise<void> {
    try {
      await api(`/v1/portal/mail/webhooks/${id}`, { method: 'PATCH', body: form });
      setModal(null);
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setErrorDetail(t('error.invalidUrl'));
      } else {
        setErrorDetail(t('error.update'));
      }
    }
  }

  async function rotateSecret(id: string): Promise<void> {
    try {
      const minted = await api<WebhookWithSecret>(
        `/v1/portal/mail/webhooks/${id}/rotate-secret`,
        { method: 'POST' },
      );
      setModal({ kind: 'reveal', minted, rotated: true });
    } catch {
      setErrorDetail(t('error.rotate'));
    }
  }

  async function deleteWebhook(id: string): Promise<void> {
    try {
      await api(`/v1/portal/mail/webhooks/${id}`, { method: 'DELETE' });
      setModal(null);
      setData((prev) =>
        prev ? { ...prev, webhooks: prev.webhooks.filter((w) => w.id !== id) } : prev,
      );
    } catch {
      setErrorDetail(t('error.delete'));
    }
  }

  async function testWebhook(id: string): Promise<void> {
    setTestFlash(t('test.testing', { id }));
    try {
      await api(`/v1/portal/mail/webhooks/${id}/test`, { method: 'POST' });
      setTestFlash(t('test.queued'));
    } catch {
      setTestFlash(t('error.test'));
    }
  }

  async function openDeliveries(webhook: WebhookSummary): Promise<void> {
    setModal({ kind: 'deliveries', webhook, rows: null });
    try {
      const r = await api<{ deliveries: DeliveryRow[] }>(
        `/v1/portal/mail/webhooks/${webhook.id}/deliveries`,
      );
      setModal({ kind: 'deliveries', webhook, rows: r.deliveries });
    } catch {
      setErrorDetail(t('error.deliveries'));
      setModal(null);
    }
  }

  if (data && !data.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{t('unavailable.title')}</h2>
        <p className="mt-1">{t('unavailable.text')}</p>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-5 flex items-start justify-between gap-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('intro')}</p>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModal({ kind: 'create' })}
            className="shrink-0 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {t('add')}
          </button>
        )}
      </header>

      <MailReadOnlyNotice />

      {errorDetail && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}
      {testFlash && (
        <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-700">
          {testFlash}
        </div>
      )}

      {state === 'loading' && <p className="text-sm text-gray-500">{t('loading')}</p>}

      {state === 'idle' && data && data.webhooks.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          {t('empty')}
        </div>
      )}

      {state === 'idle' && data && data.webhooks.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('table.url')}</th>
                <th className="px-3 py-2">{t('table.events')}</th>
                <th className="px-3 py-2">{t('table.status')}</th>
                <th className="px-3 py-2">{t('table.lastDelivery')}</th>
                <th className="px-3 py-2 text-right">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.webhooks.map((w) => (
                <tr key={w.id}>
                  <td className="px-3 py-2">
                    {w.label && (
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{w.label}</div>
                    )}
                    <code className="block truncate text-xs text-gray-700 dark:text-gray-300">{w.url}</code>
                  </td>
                  <td className="px-3 py-2">
                    {w.event_filters.length === 0 ? (
                      <span className="text-xs text-gray-500">{t('table.allEvents')}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {w.event_filters.map((e) => (
                          <span
                            key={e}
                            className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-300"
                          >
                            {e}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded border px-1.5 py-0.5 text-xs ${
                        w.enabled
                          ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
                          : 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {w.enabled ? t('table.enabled') : t('table.disabled')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {w.last_delivery_status ?? '—'}
                  </td>
                  <td className="space-x-2 px-3 py-2 text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => void testWebhook(w.id)}
                        className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
                      >
                        {t('table.test')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void openDeliveries(w)}
                      className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
                    >
                      {t('table.deliveries')}
                    </button>
                    {canWrite && (
                      <>
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'edit', webhook: w })}
                          className="text-xs text-brand-500 hover:underline"
                        >
                          {t('table.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => void rotateSecret(w.id)}
                          className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        >
                          {t('table.rotate')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'confirm-delete', webhook: w })}
                          className="text-xs text-red-700 dark:text-red-300 hover:underline"
                        >
                          {t('table.delete')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SignatureHelp />

      {modal?.kind === 'create' && (
        <WebhookFormModal
          title={t('form.addTitle')}
          initial={{ url: '', label: '', event_filters: [], enabled: true }}
          onCancel={() => setModal(null)}
          onSubmit={(v) => void createWebhook(v)}
        />
      )}
      {modal?.kind === 'edit' && (
        <WebhookFormModal
          title={t('form.editTitle')}
          initial={{
            url: modal.webhook.url,
            label: modal.webhook.label ?? '',
            event_filters: modal.webhook.event_filters,
            enabled: modal.webhook.enabled,
          }}
          onCancel={() => setModal(null)}
          onSubmit={(v) => void patchWebhook(modal.webhook.id, v)}
        />
      )}
      {modal?.kind === 'reveal' && (
        <RevealSecretModal
          minted={modal.minted}
          rotated={modal.rotated}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'deliveries' && (
        <DeliveriesModal
          webhook={modal.webhook}
          rows={modal.rows}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'confirm-delete' && (
        <ConfirmDeleteModal
          webhook={modal.webhook}
          onCancel={() => setModal(null)}
          onConfirm={() => void deleteWebhook(modal.webhook.id)}
        />
      )}
    </div>
  );
}

interface WebhookFormValue {
  url: string;
  label?: string;
  event_filters: string[];
  enabled: boolean;
}

function WebhookFormModal({
  title,
  initial,
  onCancel,
  onSubmit,
}: {
  title: string;
  initial: WebhookFormValue;
  onCancel: () => void;
  onSubmit: (v: WebhookFormValue) => void;
}) {
  const { t } = useTranslation('mail-webhooks');
  const [form, setForm] = useState<WebhookFormValue>(initial);
  const valid = form.url.startsWith('https://') || form.url.startsWith('http://localhost');
  function toggleEvent(e: string) {
    setForm((p) => ({
      ...p,
      event_filters: p.event_filters.includes(e)
        ? p.event_filters.filter((x) => x !== e)
        : [...p.event_filters, e],
    }));
  }
  return (
    <ModalShell title={title} titleId="wh-form-title" onCancel={onCancel}>
      <label className="block">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{t('form.url')}</span>
        <input
          type="url"
          autoFocus
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          placeholder={t('form.urlPlaceholder')}
          className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
      <label className="mt-3 block">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{t('form.label')}</span>
        <input
          type="text"
          maxLength={128}
          value={form.label ?? ''}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder={t('form.labelPlaceholder')}
          className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
      <fieldset className="mt-3">
        <legend className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('form.events')}</legend>
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{t('form.eventsHelp')}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {EVENT_TYPES.map((e) => (
            <label
              key={e}
              className="flex cursor-pointer items-center gap-1.5 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              <input
                type="checkbox"
                checked={form.event_filters.includes(e)}
                onChange={() => toggleEvent(e)}
              />
              <span>{e}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="mt-4 flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
        <span className="text-sm">{t('form.enabled')}</span>
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('form.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onSubmit(form)}
          disabled={!valid}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-300"
        >
          {t('form.save')}
        </button>
      </div>
    </ModalShell>
  );
}

function RevealSecretModal({
  minted,
  rotated,
  onClose,
}: {
  minted: WebhookWithSecret;
  rotated: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('mail-webhooks');
  const [copied, setCopied] = useTransientState(false, 2000);
  function copy() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(minted.secret);
      setCopied(true);
    }
  }
  return (
    <ModalShell
      title={rotated ? t('reveal.titleRotated') : t('reveal.titleCreated')}
      titleId="wh-reveal-title"
      onCancel={onClose}
      hideCloseX
    >
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
        <strong>{t('reveal.saveNow')}</strong> {t('reveal.saveNowText')}
      </div>
      <div className="mt-4">
        <span className="block text-xs text-gray-600 dark:text-gray-400">{t('reveal.secret')}</span>
        <div className="mt-2 flex items-stretch gap-2">
          <code
            data-testid="reveal-webhook-secret"
            className="min-w-0 flex-1 overflow-x-auto rounded bg-gray-900 px-3 py-2 text-xs text-gray-100"
          >
            {minted.secret}
          </code>
          <button
            type="button"
            onClick={copy}
            className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            {copied ? t('reveal.copied') : t('reveal.copy')}
          </button>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('reveal.dismiss')}
        </button>
      </div>
    </ModalShell>
  );
}

function DeliveriesModal({
  webhook,
  rows,
  onClose,
}: {
  webhook: WebhookSummary;
  rows: DeliveryRow[] | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('mail-webhooks');
  return (
    <ModalShell
      title={t('deliveries.title', { name: webhook.label ?? webhook.url })}
      titleId="wh-deliv-title"
      onCancel={onClose}
      wide
    >
      {rows === null && <p className="text-sm text-gray-500">{t('deliveries.loading')}</p>}
      {rows !== null && rows.length === 0 && (
        <p className="text-sm text-gray-500">{t('deliveries.empty')}</p>
      )}
      {rows !== null && rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('deliveries.time')}</th>
                <th className="px-3 py-2">{t('deliveries.event')}</th>
                <th className="px-3 py-2">{t('deliveries.http')}</th>
                <th className="px-3 py-2">{t('deliveries.attempts')}</th>
                <th className="px-3 py-2">{t('deliveries.excerpt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 text-xs tabular-nums text-gray-500">
                    {fmtDateTime(d.delivered_at, '—')}
                  </td>
                  <td className="px-3 py-2 text-xs">{d.event}</td>
                  <td className="px-3 py-2 text-xs">
                    <DeliveryStatusPill status={d.response_status} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {d.attempts}
                    {d.is_retry && <span className="ml-1 text-amber-700">↻</span>}
                  </td>
                  <td className="px-3 py-2 truncate text-xs text-gray-600 dark:text-gray-400">
                    {d.response_excerpt ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('deliveries.close')}
        </button>
      </div>
    </ModalShell>
  );
}

function DeliveryStatusPill({ status }: { status: number | null }) {
  const { t } = useTranslation('mail-webhooks');
  if (status === null) {
    return <span className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-red-800 dark:text-red-300">{t('deliveries.networkErr')}</span>;
  }
  const tone =
    status >= 200 && status < 300
      ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
      : status >= 500
        ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300'
        : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200';
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 ${tone}`}>{status}</span>
  );
}

function ConfirmDeleteModal({
  webhook,
  onCancel,
  onConfirm,
}: {
  webhook: WebhookSummary;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('mail-webhooks');
  return (
    <ModalShell
      title={t('delete.title', { name: webhook.label ?? webhook.url })}
      titleId="wh-del-title"
      onCancel={onCancel}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('delete.text')}
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('delete.cancel')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          {t('delete.delete')}
        </button>
      </div>
    </ModalShell>
  );
}

function SignatureHelp() {
  const { t } = useTranslation('mail-webhooks');
  return (
    <details className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100">
        {t('signature.summary')}
      </summary>
      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
        {t('signature.p1')}{' '}
        <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">X-orboto-Signature</code>{' '}
        {t('signature.p2')}{' '}
        <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">t=&lt;unix&gt;, v1=&lt;hex&gt;</code>{' '}
        {t('signature.p3')} <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">v1</code> {t('signature.p4')}{' '}
        <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">HMAC-SHA256(secret, t + '.' + raw_body)</code>.{' '}
        {t('signature.p5')}
      </p>
      <pre className="mt-3 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
{`import { createHmac, timingSafeEqual } from 'node:crypto';

function verify(rawBody, header, secret) {
  const [t, v1] = header.split(', ').map((s) => s.split('=')[1]);
  if (Math.abs(Date.now() / 1000 - Number(t)) > 300) return false;
  const expected = createHmac('sha256', secret)
    .update(\`\${t}.\${rawBody}\`)
    .digest('hex');
  return timingSafeEqual(Buffer.from(expected), Buffer.from(v1));
}`}
      </pre>
    </details>
  );
}

function ModalShell({
  title,
  titleId,
  children,
  onCancel,
  wide,
  hideCloseX,
}: {
  title: string;
  titleId: string;
  children: React.ReactNode;
  onCancel: () => void;
  wide?: boolean;
  hideCloseX?: boolean;
}) {
  const { t } = useTranslation('mail-webhooks');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className={`w-full ${wide ? 'max-w-4xl' : 'max-w-lg'} rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl`}
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="truncate text-base font-semibold">{title}</h2>
          {!hideCloseX && (
            <button
              type="button"
              onClick={onCancel}
              aria-label={t('close')}
              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            >
              ✕
            </button>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}
